import React, { useEffect, useRef } from 'react';
import { Button, Divider } from 'antd';
import { LoginOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { isLocalDevelopment } from '@/utils/env';
import './LoginPage1.css';

// 配置常量
const LARK_CONFIG = {
  APP_ID: import.meta.env.VITE_LARK_APP_ID || 'cli_aa3f7b88cc389e17',
  SCOPE: '',
  STATE: '',
  QR_SCRIPT_URL:
    'https://lf-package-us.larksuitecdn.com/obj/lark-static-us/lark/passport/qrcode/LarkSSOSDKWebQRCode-1.0.3.js',
  AUTH_BASE_URL: 'https://open.larksuite.com/open-apis/authen/v1/authorize',
  PASSPORT_URL: 'https://passport.larksuite.com/suite/passport/oauth/authorize',
} as const;

// QR登录配置
const getQRConfig = () => {
  const redirectUri = `${window.location.origin}/callback`;
  return {
    clientId: LARK_CONFIG.APP_ID,
    redirectUri,
    state: LARK_CONFIG.STATE,
    goto: `${LARK_CONFIG.PASSPORT_URL}?client_id=${LARK_CONFIG.APP_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=${LARK_CONFIG.STATE}`,
  };
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const qrCodeContainerRef = useRef<HTMLDivElement>(null);
  const qrConfig = getQRConfig();

  const buildLarkAuthUrl = () => {
    const params = new URLSearchParams({
      app_id: LARK_CONFIG.APP_ID,
      redirect_uri: qrConfig.redirectUri,
      scope: LARK_CONFIG.SCOPE,
      state: LARK_CONFIG.STATE,
    });
    return `${LARK_CONFIG.AUTH_BASE_URL}?${params.toString()}`;
  };

  // 点击登录按钮的处理函数
  const handleLogin = () => {
    // 绕过登录
    // 根据地址栏 hostname 判断：localhost 或 192.168 开头使用 localStorage，否则使用飞书认证
    if (isLocalDevelopment()) {
      // 本地开发环境：使用 localStorage 设置用户信息，然后导航到首页
      localStorage.setItem(
        'userInfo',
        '{"id":"B5878549AE66E6C238E5E9240BDAEADBCD298A23D4061A891324E4D2F2DADDFCA9276D9BCF6A5B82D846C025F1A21F5A996A4A95F4AE447C918A6CF4A9C17C518510F4308CB8CD6D","name":"Deng.Lingxiao","avatar":"https://s16-imfile-sg.feishucdn.com/static-resource/v1/v3_00mb_1e0171fd-6baf-4ed5-9a5e-d619311b96hu~?image_size=72x72&cut_type=&quality=&format=image&sticker_format=.webp"}'
      );
      localStorage.setItem('userLoggedIn', 'true');
      navigate('/', { replace: true });
    } else {
      // 非本地环境：使用飞书认证
      window.location.href = buildLarkAuthUrl();
    }
  };

  // 处理消息事件，用于二维码登录
  const handleMessage = (event: MessageEvent) => {
    const qrLogin = window.QRLoginObj;
    if (qrLogin?.matchOrigin(event.origin) && qrLogin?.matchData(event.data)) {
      const loginTmpCode = event.data.tmp_code;
      window.location.href = `${qrConfig.goto}&tmp_code=${loginTmpCode}`;
    }
  };

  useEffect(() => {
    const loadQRScript = () => {
      if (document.getElementById('lark-sso-qrcode-script')) return;

      const script = document.createElement('script');
      script.src = LARK_CONFIG.QR_SCRIPT_URL;
      script.async = true;
      script.id = 'lark-sso-qrcode-script';
      script.onload = initQRLogin;
      document.body.appendChild(script);
    };

    const initQRLogin = () => {
      if (!window.QRLogin || !qrCodeContainerRef.current) return;

      window.QRLoginObj = window.QRLogin({
        id: qrCodeContainerRef.current.id,
        goto: qrConfig.goto,
        width: '300',
        height: '300',
        style: `
          width: 300px;
          height: 300px;
          border-width: 0px;
          overflow: hidden;
        `,
      });
    };

    loadQRScript();
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [qrConfig.goto]);

  return (
    <div className="login-page">
      <div className="login-glass">
        <h1 className="login-title">Supp Admin</h1>
        <p className="login-subtitle">Manage Supp Content</p>
        {/* 普通登录按钮 */}
        <Button
          type="primary"
          icon={<LoginOutlined />}
          size="large"
          className="login-button"
          onClick={handleLogin}
        >
          Login with Lark
        </Button>

        <Divider className="login-divider">Or</Divider>

        {/* 二维码登录 */}
        <div className="qr-code-container">
          <h2 className="qr-title">Scan QR Code to Login</h2>
          <div
            id="qr-code-container"
            ref={qrCodeContainerRef}
            className="qr-code-box"
          />
        </div>
      </div>
    </div>
  );
};

// 添加类型定义
declare global {
  interface Window {
    QRLogin: any;
    QRLoginObj: any;
  }
}

export default LoginPage;
